import { defineComponent as _defineComponent } from 'vue'
import PortalVue from 'portal-vue';
import Vue, { computed, ComputedRef, defineAsyncComponent, Ref, ref } from 'vue';

import ChatSummaryDialog from '@/components/dialogs/ChatSummaryDialog.vue';
import { ViewableEntry } from '@/components/files/files.types';
import PushNotificationPermissionBanner from '@/components/pushmessaging/PushNotificationPermissionBanner.vue';
import { useRoute } from '@/composables/router/VueRouter';
import { AppName } from '@/data/Branding';
import BrowserDetection from '@/data/BrowserDetection';
import { Track } from '@/data/datatypes/Track';
import HeaderBar from '@/HeaderBar.vue';
import NonAppMenu from '@/NonAppMenu.vue';
import RouteNames from '@/router/RouteNames';
import { useKnowledgeBaseStore } from '@/stores/KnowledgeBase';
import { usePushNotificationStore } from '@/stores/PushNotification';
import { useTrackEntriesStore } from '@/stores/TrackEntries';
import { useTracksStore } from '@/stores/Tracks';
import { useUIStateStore } from '@/stores/UIState';
import { useUserStore } from '@/stores/User';

interface Props {
  inPhoneLandscapeMode?: boolean;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'TopLevelLayout',
  props: {
    inPhoneLandscapeMode: { type: Boolean, default: false }
  },
  setup(__props: any) {

const FileViewDialog = defineAsyncComponent(() => import('@/components/dialogs/FileViewDialog.vue'));
const ConfirmDialog = defineAsyncComponent(() => import('@/components/dialogs/ConfirmDialog.vue'));

const tracksStore = useTracksStore();
const trackEntriesStore = useTrackEntriesStore();
const userStore = useUserStore();
const uiStateStore = useUIStateStore();
const pushNotificationStore = usePushNotificationStore();
const knowledgeBaseStore = useKnowledgeBaseStore();

const route = useRoute();

Vue.use(PortalVue);



const showClearAllNotifications: Ref<boolean> = ref(false);

const pushNotificationStillDenied: ComputedRef<boolean> = computed(() => {
  return pushNotificationStore.pushNotificationStillDenied;
});

function clearPushNotificationStillDenied(): Promise<void> {
  return pushNotificationStore.clearPushNotificationStillDenied();
}

const privacyMode: ComputedRef<boolean> = computed(() => {
  return uiStateStore.privacyMode;
});

const hideHeaderBar: ComputedRef<boolean> = computed(() => {
  return uiStateStore.hideHeaderBar;
});

const sideMenuOpen: ComputedRef<boolean> = computed(() => {
  return uiStateStore.sideMenuOpen;
});

const isGuestUser: ComputedRef<boolean> = computed(() => {
  return userStore.isGuestUser;
});

const currentlyDisplayedChatSummaryTrack: ComputedRef<Track | null> = computed(() => {
  return tracksStore.currentlyDisplayedChatSummaryTrack;
});

const currentlyDisplayedFile: ComputedRef<ViewableEntry | null> = computed(() => {
  return trackEntriesStore.currentlyDisplayedFile;
});

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const showFooterMenu: ComputedRef<boolean> = computed(() => {
  return isMobile.value && BrowserDetection.isSupportedBrowser();
});

const pushNotificationStillDeniedMsg: ComputedRef<string> = computed(() => {
  return `${AppName} cannot sent push notifications to this device. To allow ${AppName} to push notifications,' +
    ' update your browser notification settings.`;
});

function setCurrentlyDisplayedChatSummaryTrack(track: Track | null): void {
  return tracksStore.setCurrentlyDisplayedChatSummaryTrack(track);
}

function clearAllNotifications(): void {
  showClearAllNotifications.value = false;
  tracksStore.clearAllUnseenTrackActivity();
  knowledgeBaseStore.clearAllKnowledgeBaseNotifications();
}

const isServerMessage: ComputedRef<boolean> = computed(() => {
  return route.name === RouteNames.SERVER_MESSAGE_ROUTE_NAME;
});

const showPushPermissionRequest: ComputedRef<boolean> = computed(() => {
  if (isGuestUser.value) {
    return false;
  }

  // If the browser doesn't support notifications, or it was dismissed on a previous app load (flagged in local
  // storage) don't show the banner.
  if (pushNotificationStore.pushNotificationState === null ||
      !pushNotificationStore.pushNotificationState.supported ||
      pushNotificationStore.pushBannerDismissed) {
    return false;
  }

  // Only show the permissions required banner if the user has notifications enabled (in their CafeX settings)
  // for the current device type.
  const isMobileDevice = BrowserDetection.isMobile();
  const userWantsNotificationsForThisDeviceType = userStore.currentUser &&
    ((userStore.currentUser.chatMobilePushNotifications && isMobileDevice) ||
    (userStore.currentUser.chatDesktopPushNotifications && !isMobileDevice));

  // If their settings suggest they want notifications here, show the banner if we need to
  if (userWantsNotificationsForThisDeviceType) {
    return !pushNotificationStore.pushBannerDismissed && !pushNotificationStore.pushNotificationState.enabled;
  } else {
    return false;
  }
});

function setCurrentlyDisplayedFile(file: ViewableEntry | null): void {
  trackEntriesStore.setCurrentlyDisplayedFile(file);
}

return { __sfc: true,FileViewDialog, ConfirmDialog, tracksStore, trackEntriesStore, userStore, uiStateStore, pushNotificationStore, knowledgeBaseStore, route, showClearAllNotifications, pushNotificationStillDenied, clearPushNotificationStillDenied, privacyMode, hideHeaderBar, sideMenuOpen, isGuestUser, currentlyDisplayedChatSummaryTrack, currentlyDisplayedFile, isMobile, showFooterMenu, pushNotificationStillDeniedMsg, setCurrentlyDisplayedChatSummaryTrack, clearAllNotifications, isServerMessage, showPushPermissionRequest, setCurrentlyDisplayedFile, ChatSummaryDialog, PushNotificationPermissionBanner, HeaderBar, NonAppMenu }
}

})